<template>
    <slot />
</template>

<script setup>
    import { useLoaderComplete } from '#imports';
    import { gsap } from 'gsap';
    import {
        AdditiveBlending,
        Color,
        DoubleSide,
        FrontSide,
        LinearMipmapLinearFilter,
        Mesh,
        Object3D,
        PlaneGeometry,
    } from 'three';
    import { onMounted, ref, watch, watchEffect } from 'vue';

    import glslConst from '@resn/gozer-glsl/math/const.glsl';
    import glslCircle from '@resn/gozer-glsl/shapes/circle.glsl';
    import glslDraw from '@resn/gozer-glsl/shapes/draw.glsl';
    import glslPoly from '@resn/gozer-glsl/shapes/poly.glsl';
    import glslRect from '@resn/gozer-glsl/shapes/rect.glsl';
    import { toRadians } from '@resn/gozer-math';
    import { simplePlaneGeometry, simpleVs } from '@resn/gozer-three';
    import { CustomMaterial } from '@resn/gozer-three/materials';
    import { usePane, useRafBool, useResize, useThreeObject } from '@resn/gozer-vue';

    import { createPatternBg } from '~/components/gl/Cards/reveals/wax-chrome/modules/pattern-bg/create-pattern-bg';
    import SimplePlane from '~/components/gl/common/SimplePlane.vue';
    import { createPulseGradientRefs } from '~/components/gl/shaders/ShaderChunks/radial-pulse-gradient';
    import { LAYER_BG } from '~/core/constants';
    import { loadBitmapTexture } from '~/libs/three/loadBitmapTexture';
    import { useColorPalette } from '~/providers/ColorPaletteProvider';

    const props = defineProps({
        // texture: { default: '/images/sample-patterns/hexagon@lg.webp' }, // hexagon-single | dot-single | hexagon
        texture: { default: null },
        color: { type: String, default: '#7dd2ff' },
        visible: { type: Boolean, default: true },
        active: { type: Boolean, default: true },
    });

    const patterns = ['circle', 'square', 'triangle', 'diamond'];

    const BASE_ANGLE = 60;

    const active = ref(props.active);
    const visible = ref(props.visible);
    const color = ref(props.color);

    const pattern = ref('circle');
    const aspect = ref(1);
    const colorProgress = ref(0);

    const alpha = ref(0.05);
    const radius = ref(3);
    const scale = ref(20);
    const angle = ref(BASE_ANGLE);
    const angleIn = ref(5);

    const repeat = ref(100);
    const shapeScale = ref(0.85); // 0.85
    const warpStrength = ref(0.015);
    const warpFreq = ref(1.5);
    const warpSpeed = ref(0.5);
    const maskAngle = ref(0);
    const maskEdge = ref(0.15);
    const depthScaleStartThreshold = ref(0.35);

    const planeSegments = ref(30);

    const { object } = useThreeObject(null, { props: { v: visible } });
    const basePlane = new PlaneGeometry(1, 1, planeSegments.value, planeSegments.value);

    const { uPulseProgress, uPulseThickness, uPulseFalloff, uPulseMaxReach } =
        createPulseGradientRefs('PatternBg Pulse');
    uPulseFalloff.value = 0.2;
    uPulseThickness.value = 0.25;
    const pulseAlpha = ref(0.85);

    const { colorPalette } = useColorPalette();

    const createPlane = (index) => {
        const material = createPatternBg({
            alpha: alpha.value,
            transparent: true,
            wireframe: false,
            vertexDistortion: true,
            useDepthScale: true,
            color: color.value,
            depthScaleStartThreshold: depthScaleStartThreshold.value,
        });
        // material.blending = AdditiveBlending;
        const obj = new Object3D();
        const mesh = new Mesh(basePlane, material);
        mesh.renderOrder = -1;
        obj.add(mesh);
        object.add(obj);

        material.side = DoubleSide;
        material.uniforms.uColor.value = colorPalette[0];
        mesh.rotation.y = toRadians(-90);

        return { obj, mesh, material, index };
    };

    const planes = new Array(2).fill(0).map(createPlane);

    onMounted(() => {
        setTexture(props.texture);
    });

    // Function to set texture
    const setTexture = (url) => {
        if (!url) {
            console.warn('No image URL provided for texture');

            planes.forEach(({ material }) => {
                material.uniforms.uUseTexture.value = false;
            });
            return;
        }

        loadBitmapTexture(url, { repeatWrapping: true }).then((texture) => {
            // repeat.value = 10;
            planes.forEach(({ material }) => {
                material.uniforms.uTexture.value = texture;
                material.uniforms.uUseTexture.value = true;
            });
        });
    };

    let tl;
    const show = ({
        delay = 0,
        alpha: alphaBase = 0.05,
        addPulse = false,
        pulseDelay = 0,
        duration = 1,
        ease = 'sine.out',
        outEase = 'sine.inOut',
        outDuration = 1,
        outStart = 2,
    } = {}) => {
        tl?.kill();
        tl = gsap.timeline({ delay });

        if (addPulse)
            tl.fromTo(
                uPulseProgress,
                { value: 0 },
                { value: 1, duration: 2.2, ease: 'power2.inOut' },
                pulseDelay
            );

        tl.to(alpha, { value: alphaBase, duration: duration, ease }, 0);
        tl.to(colorProgress, { value: 1, duration: 0.5, ease }, 0);

        tl.to(alpha, { value: 0.05, duration: outDuration, ease: outEase }, outStart);
        tl.to(colorProgress, { value: 0, duration: outDuration, ease: outEase }, outStart);
    };

    const fadeIn = ({
        alpha: alphaTarget = 0.05,
        duration = 0.8,
        ease = 'sine.out',
        delay = 0,
    } = {}) => {
        tl?.kill();
        tl = gsap.timeline({ delay });
        tl.to(alpha, { value: alphaTarget, duration, ease }, 0);
        tl.to(colorProgress, { value: 1, duration: duration * 0.6, ease }, 0);
        return tl;
    };

    const hide = ({
        duration = 0.5,
        ease = 'sine.inOut',
        alpha: alphaTarget = 0,
        onComplete,
    } = {}) => {
        tl?.kill();
        tl = gsap.timeline({ onComplete });
        tl.to(alpha, { value: alphaTarget, duration, ease });
        tl.to(colorProgress, { value: 0, duration, ease }, 0);
    };

    useResize(({ width, height }) => {
        aspect.value = width / Math.max(height, 1.0);
    }, true);

    useRafBool(active, ({ timestamp }) => {
        planes.forEach(({ material }) => {
            material.uniforms.uAlpha.value = alpha.value;
            material.uniforms.uColorProgress.value = colorProgress.value;
            material.uniforms.uTime.value = timestamp * 0.001;
        });
    });

    const pointOnCircle = (angle, radius) => {
        return {
            x: radius * Math.cos(angle),
            y: radius * Math.sin(angle),
        };
    };

    watchEffect(() => {
        planes.forEach(({ obj, mesh }, index) => {
            const a = angle.value + (index * 360) / planes.length;
            const point = pointOnCircle(toRadians(a), radius.value);
            obj.position.set(point.x, point.y, 0);
            obj.rotation.z = toRadians(a);
            mesh.rotation.y = toRadians(-90 + angleIn.value);
        });

        // object.rotation.z = toRadians(angle.value);
    });

    watchEffect(() => {
        planes.forEach(({ material, mesh }) => {
            mesh.scale.setScalar(scale.value);
            // material.uniforms.uColor.value = new Color(color.value);
            material.uniforms.uAspect.value = aspect.value;
            material.uniforms.uRepeat.value = repeat.value;
            material.uniforms.uPattern.value = patterns.indexOf(pattern.value);
            material.uniforms.uShapeScale.value = shapeScale.value;
            material.uniforms.uWarpStrength.value = warpStrength.value;
            material.uniforms.uWarpFreq.value = warpFreq.value;
            material.uniforms.uWarpSpeed.value = warpSpeed.value;
            material.uniforms.uMaskAngle.value = maskAngle.value;
            material.uniforms.uMaskEdge.value = maskEdge.value;
            material.uniforms.uDepthScaleStartThreshold.value = depthScaleStartThreshold.value;
            material.uniforms.uPulseAlpha.value = pulseAlpha.value;
        });
    });

    watchEffect(() => {
        planes.forEach(({ material, mesh }) => {
            material.uniforms.uPulseProgress.value = uPulseProgress.value;
            material.uniforms.uPulseThickness.value = uPulseThickness.value;
            material.uniforms.uPulseFalloff.value = uPulseFalloff.value;
            material.uniforms.uPulseMaxReach.value = uPulseMaxReach.value;
        });
    });

    useRafBool(active, ({ timestamp }) => {
        planes.forEach(({ material }) => {
            material.uniforms.uMaskAngle.value = maskAngle.value + timestamp * 0.0005;
        });
        // angle.value += 0.1;
        // angle.value = 90 + Math.sin(timestamp * 0.0002) * 20;
    });

    watch(
        () => props.active,
        (v) => (active.value = v)
    );
    watch(
        () => props.visible,
        (v) => (visible.value = v)
    );
    watch(
        () => props.color,
        (c) => (color.value = c)
    );
    watch(() => props.texture, setTexture);

    // const pane = usePane(
    //     [
    //         { name: 'visible', value: visible },
    //         { name: 'alpha', value: alpha, options: { min: 0, max: 1, step: 0.01 } },
    //         { name: 'radius', value: radius, options: { min: 0, max: 10, step: 0.01 } },
    //         { name: 'scale', value: scale, options: { min: 0, max: 30, step: 0.01 } },
    //         { name: 'angle', value: angle, options: { min: 0, max: 360, step: 1 } },
    //         { name: 'angleIn', value: angleIn, options: { min: 0, max: 20, step: 0.1 } },
    //         { name: 'color', value: color },
    //         {
    //             name: 'repeat',
    //             value: repeat,
    //             options: { min: 2, max: 100, step: 1 },
    //         },
    //         { name: 'shapeScale', value: shapeScale, options: { min: 0, max: 10, step: 0.01 } },
    //         {
    //             name: 'warpStrength',
    //             value: warpStrength,
    //             options: { min: 0, max: 0.1, step: 0.001 },
    //         },
    //         { name: 'warpFreq', value: warpFreq, options: { min: 0, max: 2, step: 0.001 } },
    //         { name: 'warpSpeed', value: warpSpeed, options: { min: 0, max: 2, step: 0.01 } },
    //         { name: 'maskAngle', value: maskAngle, options: { min: 0, max: Math.PI, step: 0.01 } },
    //         { name: 'maskEdge', value: maskEdge, options: { min: 0.01, max: 1.0, step: 0.01 } },
    //         {
    //             name: 'depthScaleStartThreshold',
    //             value: depthScaleStartThreshold,
    //             options: { min: 0.0, max: 1.0, step: 0.01 },
    //         },
    //         { name: 'pulseAlpha', value: pulseAlpha, options: { min: 0, max: 10, step: 0.01 } },
    //     ],
    //     {
    //         title: 'PatternBg',
    //     }
    // );

    // if (pane) {
    //     pane.addBlade({
    //         view: 'list',
    //         label: 'Pattern',
    //         options: patterns.map((p) => ({ text: p, value: p })),
    //         value: pattern.value,
    //     }).on('change', (e) => {
    //         pattern.value = e.value;
    //     });
    // }

    defineExpose({ show, hide, fadeIn });
</script>
